import React, { useEffect, useState } from 'react';
import {
  Drawer, Form, Input, InputNumber, Row, Col, Button,
  Select, Tag, Divider, Spin
} from 'antd';
import {
  AppstoreOutlined, CloseOutlined, DollarOutlined,
  FileTextOutlined, SettingOutlined, TagOutlined, CarOutlined,
  FolderOpenOutlined, LoadingOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { serviceType } from '../../actions/constants/constant';
import { getTenantId } from '../../services/helper';

export interface ServiceInput {
  name: string;
  description: string;
  duration: string;
  price: number;
  tenantId: string;
  category: 'LMMV' | 'HMV' | 'Two Wheeler' | 'Other';
}

interface ServiceFormModalProps {
  open: boolean;
  title: string;
  onCancel: () => void;
  onFinish: (values: ServiceInput) => void;
  form: any;
  initialValues?: Partial<ServiceInput>;
  isLoading?: boolean;   // ← add
}

const ServiceFormModal: React.FC<ServiceFormModalProps> = ({
  open, title, onCancel, onFinish, form, initialValues, isLoading,  // ← add
}) => {

  const documentFieldName = 'requiredDocuments';
  const categories = useSelector((state: any) => state.getCategoryReducer.categorydata);
  const [tags, setTags] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');

  const handleAdd = (raw?: string) => {
    const next = String(raw ?? inputValue).trim();
    if (!next) return;
    if (!tags.includes(next)) {
      const nextTags = [...tags, next];
      setTags(nextTags);
      form.setFieldsValue({ [documentFieldName]: nextTags });
      form.validateFields([documentFieldName]).catch(() => undefined);
    }
    setInputValue('');
  };

  const handleFinish = (values: any) => {
    onFinish({ ...values});
  };

  const handleClose = (removedTag: any) => {
    const nextTags = tags.filter((tag) => tag !== removedTag);
    setTags(nextTags);
    form.setFieldsValue({ [documentFieldName]: nextTags });
    form.validateFields([documentFieldName]).catch(() => undefined);
  };

  useEffect(() => {
    if (open) {
      form.resetFields();
      setTags([]);
      setInputValue('');

      if (initialValues) {
        form.setFieldsValue(initialValues as ServiceInput);
        const docsVal = (initialValues as any)?.[documentFieldName] ?? (initialValues as any)?.document;
        if (docsVal) {
          const initialTags = Array.isArray(docsVal) ? docsVal.map(String) : [String(docsVal)];
          setTags(initialTags);
          form.setFieldsValue({ [documentFieldName]: initialTags });
        }
      }
    } else {
      setTags([]);
      setInputValue('');
    }
  }, [open, initialValues, form]);

  return (
    <Drawer
      className="svc-drawer"
      open={open}
      onClose={onCancel}
      width={window.innerWidth > 1400 ? 530 : 480}
      closable={false}
      destroyOnClose
      title={null}
      footer={
        <div className="svc-drawer-footer">
          <Button className="svc-btn-cancel" onClick={onCancel} disabled={!!isLoading}>
            Cancel
          </Button>
          <Button
            className="svc-btn-save"
            type="primary"
            onClick={() => form.submit()}
            loading={!!isLoading}
            disabled={!!isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Service'}
          </Button>
        </div>
      }
    >
      {/* ── Spinner Overlay ── */}
      {isLoading && (
        <div className="svc-spinner-overlay">
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 40, color: 'var(--primary-color)' }} spin />}
          />
          <span style={{ color: 'var(--primary-color)', fontWeight: 500, fontSize: 14 }}>
            Saving service...
          </span>
        </div>
      )}

      {/* ── Sticky Header ── */}
      <div className="svc-drawer-header">
        <div className="svc-drawer-header-inner">
          <div className="svc-drawer-title-group">
            <div className="svc-drawer-icon-box">
              <FolderOpenOutlined />
            </div>
            <div className="svc-drawer-title-text">
              <span className="svc-drawer-title">{title}</span>
              <span className="svc-drawer-subtitle">Fill in the details to create or update a service</span>
            </div>
          </div>
          <button className="svc-drawer-close-btn" onClick={onCancel} aria-label="Close">
            <CloseOutlined />
          </button>
        </div>
        <Divider className="svc-drawer-header-divider" />
      </div>

      <Form form={form} layout="vertical" onFinish={handleFinish} className="svc-form">

        {/* ══ Section: Basic Information ══ */}
        <div className="svc-form-section">
          

          <Form.Item
            name="serviceName"
            label="Service Name"
            rules={[{ required: true, message: 'Please enter service name' }]}
            className="svc-form-item"
          >
            <Input className="svc-input" placeholder="e.g. Advanced Car Training" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="categoryId"
                label="Category"
                rules={[{ required: true, message: 'Please select a category' }]}
                className="svc-form-item"
              >
                <Select className="svc-select" placeholder="Select category">
                  {categories &&
                    categories.categories
                      .filter((r: any) => r.categoryType !== 'QUERY')
                      .map((r: any) => (
                        <Select.Option key={r._id} value={r._id}>
                          {r.categoryName}
                        </Select.Option>
                      ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Service Type"
                rules={[{ required: true, message: 'Please select a service type' }]}
                className="svc-form-item"
              >
                <Select className="svc-select" placeholder="Select type">
                  {serviceType.map((r: any) => (
                    <Select.Option key={r} value={r.toUpperCase()}>
                      {r}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Divider className="svc-section-divider" />

        {/* ══ Section: Pricing & Code ══ */}
        <div className="svc-form-section">   
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="shortName"
                label="Service Code"
                rules={[
                  { required: true, message: 'Please enter service code' },
                  { max: 10, message: 'Maximum 10 characters allowed' },
                ]}
                className="svc-form-item"
              >
                <Input className="svc-input" placeholder="e.g. 2WLT" maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="price"
                label="Price (₹)"
                rules={[{ required: true, message: 'Please enter price' }]}
                className="svc-form-item"
              >
                <InputNumber
                  className="svc-input-number"
                  placeholder="e.g. 2000"
                  min={0}
                  prefix="₹"
                />
              </Form.Item>
            </Col>
          </Row>
            <Divider className="svc-section-divider" />
        </div>

      

        {/* ══ Section: Required Documents ══ */}
        <div className="svc-form-section">
          <div className="svc-section-header">
            <span className="svc-section-title">
              Documents
              {tags.length > 0 && (
                <span className="svc-doc-count-badge">{tags.length}</span>
              )}
            </span>
          </div>

          <Form.Item name={documentFieldName} noStyle>
            <input type="hidden" />
          </Form.Item>

          <div className="svc-doc-add-row">
            <Input
              className="svc-input svc-doc-input"
              placeholder="Add a document (e.g. AADHAR, PAN CARD)"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onPressEnter={() => handleAdd()}
            />
            <Button
              className="svc-doc-add-btn"
              type="primary"
              onClick={() => handleAdd()}
            >
              Add
            </Button>
          </div>

          {tags.length > 0 ? (
            <div className="svc-tag-list">
              {tags.map((tag) => (
                <Tag
                  key={tag}
                  closable
                  onClose={() => handleClose(tag)}
                  className="svc-tag"
                >
                  {tag}
                </Tag>
              ))}
            </div>
          ) : (
            <div className="svc-doc-empty">
              <TagOutlined className="svc-doc-empty-icon" />
              <span className="svc-doc-empty-text">
                No documents added yet. Type a document name and click Add.
              </span>
            </div>
          )}
        </div>

        

        {/* ══ Section: Description ══ */}
        <div className="svc-form-section svc-form-section--last" style={{marginTop: 10}}>
        
          <Form.Item name="description" label={
              <span className="svc-label">
                Description
              </span>
            }
            className="svc-form-item"
          >
            <Input.TextArea
              className="svc-textarea"
              autoSize={{ minRows: 3, maxRows: 5 }}
              placeholder="Describe what this service covers…"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </div>

      </Form>
    </Drawer>
  );
};

export default ServiceFormModal;